const ContentFilter = {}

const mapToId = (object) => object.id
const ensureInteger = (intOrString) => parseInt(intOrString, 10)

const getRelationIds = (relation) => {
  if (!relation) return []

  if (Array.isArray(relation)) {
    return relation.map(mapToId)
  }

  return [relation.id || relation]
}

const isEmptyArray = (arr) => Array.isArray(arr) && arr.length === 0

// Returns true if both arrays contain a common element
const inclusiveFilterMatch = (entityArray, filterArray) =>
  entityArray.some((element) => filterArray.includes(element))

// returns true if entity should be visible with selected filters
export const entityPassesAccountFilters = (entity, filter) => {
  const { account: accountFilters = [], service: serviceFilters = [] } = filter
  if (isEmptyArray(accountFilters) && isEmptyArray(serviceFilters)) return true

  const entityAccountIds = getRelationIds(entity.account)
  const entityServiceIds = getRelationIds(entity.service)

  const accountFilterIds = accountFilters.map(mapToId)
  const serviceFilterIds = serviceFilters.map(mapToId)

  return (
    inclusiveFilterMatch(entityAccountIds, accountFilterIds) ||
    inclusiveFilterMatch(entityServiceIds, serviceFilterIds)
  )
}

export const entityPassesStatusFilters = (entity, filter) => {
  const { status: statusFilters = [] } = filter
  if (isEmptyArray(statusFilters)) return true

  const entityStatus = getRelationIds(entity.status)
  return inclusiveFilterMatch(entityStatus, statusFilters)
}

export const entityPassesStoryFilters = (entity, filter) => {
  const { stories: storyFilters = [] } = filter
  if (isEmptyArray(storyFilters)) return true

  const entityStoryIds = getRelationIds(entity.stories)
  const storyFilterIds = storyFilters.map(mapToId)

  return inclusiveFilterMatch(entityStoryIds, storyFilterIds)
}

export const entityPassesLabelFilters = (entity, filter) => {
  const { labels: labelFilters = [] } = filter
  if (isEmptyArray(labelFilters)) return true

  // Create a filter object keyed by label set id.
  // Each corresponding value will be an array of
  // label ids for that label set
  const filtersByLabelSet = {}
  labelFilters.forEach((label) => {
    const labelSetId = label?.label_set?.id

    // Ensure our object is initialized w/ empty array
    if (!filtersByLabelSet[labelSetId]) {
      filtersByLabelSet[labelSetId] = []
    }

    filtersByLabelSet[labelSetId].push(label.id)
  })

  const entityPassesLabelSetFilter = ([
    filterLabelSetId,
    filterLabelSetLabels,
  ]) => {
    // Label filters are exclusive between different label sets.
    // Each filter label set must have a corresponding entity
    // label within the same set. We should exit with false
    // if the filter label set is not found on the entity.
    const entityLabelSet = entity.label_sets.find(
      (set) => ensureInteger(set.id) === ensureInteger(filterLabelSetId)
    )
    if (!entityLabelSet) return false

    // The label sets match at this point, the label filter logic is inclusive.
    // Check if ANY filtered label within this set is found within the entity's label set
    const entityLabelIds = entity.labels.map(mapToId)
    return inclusiveFilterMatch(entityLabelIds, filterLabelSetLabels)
  }

  return Object.entries(filtersByLabelSet).every(entityPassesLabelSetFilter)
}

export const entityPassesBrandFilters = (entity, filter) => {
  const { brand: brandFilters = [] } = filter

  if (brandFilters.length === 0) {
    return true
  }
  if (!entity.brand) {
    return false
  }
  return brandFilters.some(
    (brandFilter) =>
      ensureInteger(brandFilter.id) === ensureInteger(entity.brand.id)
  )
}

/*
 * Takes a single content object and a filter Object
 * to determine if the content should be filtered out.
 * "filter" in this case is an object of ALL active filters.
 */
ContentFilter.matchesFilter = ({ content = {}, filter = {} }) =>
  entityPassesAccountFilters(content, filter) &&
  entityPassesStatusFilters(content, filter) &&
  entityPassesStoryFilters(content, filter) &&
  entityPassesLabelFilters(content, filter) &&
  entityPassesBrandFilters(content, filter)

ContentFilter.filter = ({ content = [], filter = {} }) =>
  content.filter((c) => ContentFilter.matchesFilter({ content: c, filter }))

export default ContentFilter
